import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../App';
import VotingButtons from './VotingButtons';

const HomePage = () => {
  const { API_BASE_URL, user } = useApp();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('newest');
  const [selectedTag, setSelectedTag] = useState('');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchQuestions();
    fetchTags();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [searchTerm, filterBy, selectedTag]);

  const fetchQuestions = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterBy) params.append('filter', filterBy);
      if (selectedTag) params.append('tag', selectedTag);
      
      const response = await fetch(`${API_BASE_URL}/questions?${params}`);
      const data = await response.json();
      
      // Ensure consistent data structure for answers, votes, and username
      const normalizedData = data.map(question => ({
        ...question,
        answers: typeof question.answers === 'number' ? question.answers : (Array.isArray(question.answers) ? question.answers.length : 0),
        votes: (question.upvotes || 0) - (question.downvotes || 0), // Calculate net votes
        username: question.author || question.username || 'Anonymous' // Use author field as username
      }));
      
      setQuestions(normalizedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tags`);
      const data = await response.json();
      // Ensure we always have a safe array structure
      const safeTags = Array.isArray(data) ? data : [];
      setTags(safeTags);
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]); // Set empty array on error
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchQuestions();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return `${Math.ceil(diffDays / 365)} years ago`;
  };

  const getFilterIcon = (filter) => {
    switch (filter) {
      case 'newest':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'score':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'unanswered':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-6 text-gray-600 text-lg">Discovering amazing questions...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-8 animate-fade-in">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-3">
              Explore <span className="text-blue-600">Questions</span>
            </h1>
            <p className="text-gray-600 text-lg">
              Find answers to your coding questions or help others solve theirs
            </p>
          </div>
          <Link 
            to="/ask"
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg btn-hover-lift btn-hover-glow btn-click-scale transition-all duration-300 flex items-center space-x-2 animate-slide-in-right animate-delay-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Ask Question</span>
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8 animate-slide-up animate-delay-100">
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl form-input-focus focus-ring transition-all duration-200"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 btn-hover-lift btn-click-scale transition-colors font-medium"
            >
              Search
            </button>
          </div>
        </form>

        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">Filter by:</span>
            <div className="flex gap-2">
              {['newest', 'score', 'unanswered'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFilterBy(filter)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm btn-hover-lift btn-click-scale transition-all duration-200 ${
                    filterBy === filter
                      ? 'bg-blue-600 text-white shadow-md animate-scale-in'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getFilterIcon(filter)}
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tags Filter */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">Tags:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedTag('')}
                className={`px-3 py-1 rounded-full text-sm font-medium btn-hover-lift btn-click-scale transition-all duration-200 ${
                  selectedTag === ''
                    ? 'bg-purple-600 text-white animate-scale-in'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {(tags || []).slice(0, 5).map((tag, index) => {
                if (!tag) return null;
                const tagName = typeof tag === 'string' ? tag : tag.name || 'Unknown';
                const tagCount = typeof tag === 'object' ? tag.count : null;
                return (
                  <button
                    key={`${tagName}-${index}`}
                    onClick={() => setSelectedTag(tagName)}
                    className={`px-3 py-1 rounded-full text-sm font-medium btn-hover-lift btn-click-scale transition-all duration-200 ${
                      selectedTag === tagName
                        ? 'bg-purple-600 text-white animate-scale-in'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tagName} {tagCount && `(${tagCount})`}
                  </button>
                );
              })}
            </div>
          </div>

          {(searchTerm || selectedTag || filterBy !== 'newest') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedTag('');
                setFilterBy('newest');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 underline font-medium"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-slide-up animate-delay-200">
        <div className="bg-white rounded-xl p-4 border border-gray-100 card-hover-glow stagger-item animate-fade-in">
          <div className="text-2xl font-bold text-blue-600">{questions.length}</div>
          <div className="text-sm text-gray-600">Questions</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 card-hover-glow stagger-item animate-fade-in">
          <div className="text-2xl font-bold text-green-600">
            {questions.filter(q => q.acceptedAnswerId).length}
          </div>
          <div className="text-sm text-gray-600">Solved</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 card-hover-glow stagger-item animate-fade-in">
          <div className="text-2xl font-bold text-purple-600">
            {questions.filter(q => (Array.isArray(q.answers) ? q.answers.length : q.answers || 0) === 0).length}
          </div>
          <div className="text-sm text-gray-600">Unanswered</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 card-hover-glow stagger-item animate-fade-in">
          <div className="text-2xl font-bold text-orange-600">{(tags || []).length}</div>
          <div className="text-sm text-gray-600">Tags</div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-6">
        {questions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No questions found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchTerm || selectedTag ? 'Try adjusting your search or filters to find what you\'re looking for.' : 'Be the first to ask a question and start the conversation!'}
            </p>
            <Link 
              to="/ask"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold btn-hover-lift btn-hover-glow btn-click-scale transition-all duration-300 inline-block animate-bounce"
            >
              Ask the First Question
            </Link>
          </div>
        ) : (
          questions.map((question, index) => (
            <div key={question.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 card-hover group stagger-item animate-fade-in">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Vote and Answer Stats */}
                <div className="flex lg:flex-col gap-6 lg:gap-4 text-center lg:min-w-24">
                  <div className="flex flex-col items-center">
                    <VotingButtons
                      itemType="question"
                      itemId={question.id}
                      initialVotes={question.votes}
                      initialUserVote={question.userVote}
                      onVoteChange={(newVotes, newUserVote) => {
                        setQuestions(questions.map(q => 
                          q.id === question.id 
                            ? { ...q, votes: newVotes, userVote: newUserVote }
                            : q
                        ));
                      }}
                      size="small"
                      showTooltip={false}
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <div className={`text-3xl font-bold mb-1 ${
                      question.acceptedAnswerId ? 'text-green-600' : 
                      (Array.isArray(question.answers) ? question.answers.length : question.answers || 0) > 0 ? 'text-blue-600' : 'text-gray-700'
                    }`}>
                      {Array.isArray(question.answers) ? question.answers.length : question.answers || 0}
                    </div>
                    <span className="text-sm text-gray-500">answers</span>
                  </div>
                </div>

                {/* Question Content */}
                <div className="flex-1">
                  <Link 
                    to={`/questions/${question.id}`}
                    className="block mb-3 group-hover:text-blue-600 transition-colors"
                  >
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors leading-tight">
                      {question.title}
                    </h3>
                  </Link>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                    {question.description.substring(0, 250)}
                    {question.description.length > 250 && '...'}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {question.tags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => setSelectedTag(tag)}
                        className="px-3 py-1 bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 text-sm rounded-full hover:from-blue-100 hover:to-purple-100 btn-hover-lift btn-click-scale transition-all duration-200 border border-blue-200 hover:border-blue-300"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>

                  {/* Question Meta */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-3">
                      <span>asked {formatDate(question.createdAt)}</span>
                      <span>by</span>
                      <span className="font-semibold text-gray-700">{question.username}</span>
                    </div>
                    
                    {question.acceptedAnswerId && (
                      <div className="flex items-center gap-2 text-green-600 font-semibold">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">Solved</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More or Pagination could go here */}
      {questions.length > 0 && (
        <div className="text-center mt-12">
          <p className="text-gray-500">
            Showing {questions.length} question{questions.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default HomePage; 